/**
 * Notification types and the useNotifications hook.
 *
 * Data sources (no dedicated /notifications endpoint exists yet):
 *   - GET /api/v1/leaves (my own leaves, last 7 days, resolved)
 *     → leave_approved, leave_rejected, leave_cancelled
 *   - GET /api/v1/leaves?scope=team&status=pending (manager view, last 24h decided)
 *     → no direct source — manager decisions are not surfaced without a dedicated audit endpoint
 *   - GET /api/v1/escalations (director view)
 *     → escalation_received for pending/escalated items
 *
 * Backend gap: there is no /notifications endpoint and no way to know
 * *when* a manager took a decision on a team leave (audit log is server-
 * side only). The hook therefore surfaces:
 *   - Employee: status changes on their own leaves (approved/rejected/cancelled)
 *   - Director/exec_director: active escalations in their queue
 *   - HR/HOD: both (own leaves + escalations visible to HR)
 * Read state is tracked client-side via localStorage.
 *
 * Once a dedicated GET /api/v1/notifications endpoint exists, replace
 * the parallel query fan-out with a single query to that endpoint.
 */

export type NotificationKind =
  | 'leave_approved'
  | 'leave_rejected'
  | 'leave_cancelled'
  | 'pattern_flagged'
  | 'escalation_received'
  | 'escalation_resolved'
  | 'wfh_submitted'
  | 'wfh_approved'
  | 'wfh_rejected'

export interface Notification {
  id: string
  kind: NotificationKind
  message: string
  /** Route to navigate when the row is clicked. */
  to?: string
  /** ISO datetime string. */
  occurredAt: string
  read: boolean
}

// ── localStorage helpers ───────────────────────────────────────────────────────

const LS_KEY = 'lms.notifications.read'

function loadReadSet(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return new Set<string>()
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) return new Set(parsed as string[])
  } catch {
    // corrupt storage — start fresh
  }
  return new Set<string>()
}

function saveReadSet(ids: Set<string>): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(ids)))
  } catch {
    // storage quota — best effort
  }
}

export function getReadSet(): Set<string> {
  return loadReadSet()
}

export function markReadInStorage(id: string): void {
  const set = loadReadSet()
  set.add(id)
  saveReadSet(set)
}

export function markAllReadInStorage(ids: string[]): void {
  const set = loadReadSet()
  for (const id of ids) set.add(id)
  saveReadSet(set)
}

// ── Notification factories ─────────────────────────────────────────────────────

import type { Leave } from '@lms/shared-types'
import type { EscalationRow } from '@lms/shared-types'
import { displayName } from './display-name'

/**
 * Derive a Notification from a leave the current employee owns,
 * when the leave has reached a terminal/notable status.
 */
export function leaveToNotification(
  leave: Leave,
  readSet: Set<string>,
): Notification | null {
  const updatedAt = leave.updatedAt
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

  if (new Date(updatedAt) < sevenDaysAgo) return null

  const leaveLabel = leave.type.charAt(0).toUpperCase() + leave.type.slice(1)

  if (leave.status === 'approved') {
    const id = `leave_approved_${leave.id}`
    return {
      id,
      kind: 'leave_approved',
      message: `Your ${leaveLabel} leave request has been approved.`,
      to: `/leave?id=${leave.id}`,
      occurredAt: updatedAt,
      read: readSet.has(id),
    }
  }

  if (leave.status === 'rejected') {
    const id = `leave_rejected_${leave.id}`
    return {
      id,
      kind: 'leave_rejected',
      message: `Your ${leaveLabel} leave request was not approved.`,
      to: `/leave?id=${leave.id}`,
      occurredAt: updatedAt,
      read: readSet.has(id),
    }
  }

  if (leave.status === 'cancelled') {
    const id = `leave_cancelled_${leave.id}`
    return {
      id,
      kind: 'leave_cancelled',
      message: `Your ${leaveLabel} leave has been cancelled.`,
      to: `/leave?id=${leave.id}`,
      occurredAt: updatedAt,
      read: readSet.has(id),
    }
  }

  return null
}

/**
 * Derive a Notification from an escalation row for a director-level user.
 */
export function escalationToNotification(
  row: EscalationRow,
  readSet: Set<string>,
): Notification {
  const id = `escalation_received_${row.id}`
  const name = displayName(row.employee)
  const leaveType = row.leave.type.charAt(0).toUpperCase() + row.leave.type.slice(1)
  return {
    id,
    kind: 'escalation_received',
    message: `${name}'s ${leaveType} leave requires your approval${
      row.blackout != null ? ` (${row.blackout.name})` : ''
    }.`,
    to: `/escalations`,
    occurredAt: row.leave.submittedAt,
    read: readSet.has(id),
  }
}

// ── Cap + sort ─────────────────────────────────────────────────────────────────

const MAX_NOTIFICATIONS = 20

export function mergeNotifications(sources: Notification[][]): Notification[] {
  const seen = new Set<string>()
  const merged: Notification[] = []

  for (const list of sources) {
    for (const n of list) {
      if (!seen.has(n.id)) {
        seen.add(n.id)
        merged.push(n)
      }
    }
  }

  merged.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
  return merged.slice(0, MAX_NOTIFICATIONS)
}
